import { type BatcherScheduler, create, windowScheduler } from '@yornaath/batshit';

import {
  AttributeDefinitionDTO,
  leaderboardClient as defaultLeaderboardClient,
  NextPageDTO,
} from '@neptune/shared/core-apis-leaderboard-domain';
import { logger as defaultLogger } from '@neptune/shared/core-logger-util';

import config from 'config';

import {
  type AttributePatternWidgetSource,
  type AttributeWidgetSource,
  WidgetSourceType,
} from './core-widget-types';
import {
  chunkDefinitionsRequests,
  compileCaseInsensitivePattern,
  DEFINITIONS_QUERY_SIZE_BUDGET_BYTES,
  type DefinitionsBatch,
  type DefinitionsBatchRequest,
  estimateDefinitionsQuerySizeBytes,
  hasAmbiguousExactMatchRanking,
  splitDefinitionsBatch,
  type TimeSlicer,
} from './widget-attribute-definitions-batch';

const CHART_MAX_METRICS_LIMIT = config.chartMaxMetricsLimit ?? 2_000;

// Widgets mount and get their run selection over a few frames; a fixed window bounds the added
// latency, which is small compared to the definitions query itself.
const BATCH_WINDOW_MS = 100;
// Bounds the per row cost of the server side exact match ranking, which evaluates every pattern.
const MAX_PATTERNS_PER_BATCH = 100;
// The server maximum of `nextPage.limit`. A batch reads a single page: every next page scans the
// runs again, so when the combined result doesn't fit, its widgets are resolved one by one right
// away, which costs one query more than without batching.
const BATCH_PAGE_SIZE = 10_000;
// Splitting a batch result runs re2js on the main thread, in slices with a turn of the event loop
// in between, so it doesn't block rendering or input however long it takes. A slice overshoots by
// at most one regex evaluation (< 0.1 ms) or the selection of one widget (~15 ms for 10000 names).
const BATCH_SPLIT_SLICE_MS = 10;
// Guards against pathological splits, checked against the literal prefilter before the first
// regex evaluation. Measured re2js costs on metric names: ~1 us per evaluation for an anchored
// literal, 15-34 us for broad patterns (`(train|val)/.*loss`, `easy@.*pass@1/`). 3000 evaluations
// per widget (45-100 ms) cost less than the widget's own query (a round trip of >= ~100 ms and a
// scan of its runs on the server); `easy@.*pass@1/` over 2880 names, a quarter of them containing
// `pass@1/`, needs ~720. All widgets of a batch wait for its whole split, which the total cap
// bounds to ~0.3-0.7 s. Over the guard, the widgets of the batch are resolved one by one.
const MAX_SPLIT_REGEX_EVALUATIONS_PER_REQUEST = 3_000;
const MAX_SPLIT_REGEX_EVALUATIONS = 20_000;

const LOGGER_MODULE = 'widgetAttributeResolver';
// Regex.validateRegex validates every pattern on its own, so the widget's own query fails too.
const INVALID_PATTERN_ERROR_TITLE_PREFIX = 'Invalid regexp';
// AttibutePathsTooLarge of ProjectRunFieldRepositoryImpl.searchDefinitions.
const QUERY_TOO_LARGE_ERROR_TITLE = /exceed max query size/i;

type Dependencies = {
  leaderboardClient: {
    queryAttributeDefinitionsWithinProject: typeof defaultLeaderboardClient.queryAttributeDefinitionsWithinProject;
  };
  batchScheduler: BatcherScheduler;
  now: () => number;
  yieldToEventLoop: () => Promise<void>;
  logger: Pick<typeof defaultLogger, 'logException' | 'logMessage'>;
};

type ResolvedNamesByRequest = Map<DefinitionsBatchRequest, Promise<string[] | undefined>>;

type UnexpectedFallbackReason =
  | 'exception'
  | 'unmatched-names'
  | 'query-too-large'
  | 'query-rejected'
  | 'batch-query-failed';

type UnexpectedFallbackContext = Record<string, string | number | undefined>;

// Unlike chained timers (clamped to 4 ms, 1 s in background tabs), messages aren't throttled.
const yieldWithMessageChannel = () =>
  new Promise<void>((resolve) => {
    if (typeof MessageChannel === 'undefined') {
      setTimeout(resolve, 0);

      return;
    }

    const { port1, port2 } = new MessageChannel();

    port1.onmessage = () => {
      port1.close();
      resolve();
    };

    port2.postMessage(undefined);
  });

export const createWidgetAttributeResolver = (dependencies: Partial<Dependencies> = {}) => {
  const {
    leaderboardClient = defaultLeaderboardClient,
    batchScheduler = windowScheduler(BATCH_WINDOW_MS),
    now = () => performance.now(),
    yieldToEventLoop = yieldWithMessageChannel,
    logger = defaultLogger,
  } = dependencies;
  const reportedFallbackReasons = new Set<UnexpectedFallbackReason>();
  // Reports of failed batch queries, made once the own query of a request of the batch succeeds.
  const batchFailureReportsByRequest = new WeakMap<DefinitionsBatchRequest, () => void>();

  // Merges definitions queries of widgets resolved at about the same time. A request without a
  // result in the batch (alone in its batch, batch cut off or failed) is resolved on its own.
  // batshit resolves all requests of a window at once when the fetcher returns, so the fetcher
  // returns a promise per request instead of waiting for the batches.
  const batcher = create({
    name: 'batshit:widgetAttributeDefinitions',
    fetcher: fetchDefinitionsBatches,
    resolver: (resolvedNamesByRequest: ResolvedNamesByRequest, request: DefinitionsBatchRequest) =>
      resolvedNamesByRequest.get(request),
    scheduler: batchScheduler,
  });

  async function fromSources({
    projectIds,
    experimentIds,
    sources,
  }: {
    projectIds: string[];
    experimentIds: string[];
    sources: AttributePatternWidgetSource[];
  }): Promise<AttributeWidgetSource[]> {
    const patterns = [...new Set(sources.map(({ value }) => value))];

    if (patterns.length === 0) {
      return [];
    }

    const request: DefinitionsBatchRequest = { projectIds, experimentIds, patterns };

    if (!isBatchable(request)) {
      return resolveUnbatched(request);
    }

    const names = await batcher.fetch(request);

    if (names) {
      return mapNamesToDomain(names);
    }

    const ownSources = resolveUnbatched(request);
    const reportBatchFailure = batchFailureReportsByRequest.get(request);

    if (reportBatchFailure) {
      // Neither delays nor fails the widget.
      void ownSources.then(reportBatchFailure, () => undefined);
    }

    return ownSources;
  }

  /**
   * Starts the query of every batch without waiting for it. A request without a batch (alone with
   * its run selection) gets no promise and is resolved on its own right away. Neither this nor the
   * returned promises reject: batshit would reject every queued request, and a batch that fails
   * (e.g. a pattern the server rejects) resolves its requests to undefined, so each of them is
   * resolved on its own and only the failing ones reject.
   */
  async function fetchDefinitionsBatches(
    requests: DefinitionsBatchRequest[],
  ): Promise<ResolvedNamesByRequest> {
    const resolvedNamesByRequest: ResolvedNamesByRequest = new Map();

    try {
      const batches = chunkDefinitionsRequests(requests, {
        maxPatterns: MAX_PATTERNS_PER_BATCH,
        maxQueryBytes: DEFINITIONS_QUERY_SIZE_BUDGET_BYTES,
      }).filter((batch) => batch.requests.length > 1);

      for (const batch of batches) {
        const namesByRequest = resolveDefinitionsBatch(batch);

        batch.requests.forEach((request) =>
          resolvedNamesByRequest.set(
            request,
            namesByRequest.then((resolvedNames) => resolvedNames?.get(request)),
          ),
        );
      }
    } catch (error) {
      // The requests without a promise are resolved on their own.
      reportUnexpectedFallback('exception', () => ({ requestCount: requests.length }), error);
    }

    return resolvedNamesByRequest;
  }

  async function resolveDefinitionsBatch(
    batch: DefinitionsBatch<DefinitionsBatchRequest>,
  ): Promise<Map<DefinitionsBatchRequest, string[]> | undefined> {
    let names: string[] | undefined;

    try {
      names = await queryDefinitionsBatch(batch);
    } catch (error) {
      await reportFailedBatchQuery(error, batch);

      return undefined;
    }

    if (!names) {
      return undefined;
    }

    const nameCount = names.length;

    try {
      const split = await splitDefinitionsBatch(batch, names, {
        maxAttributes: CHART_MAX_METRICS_LIMIT,
        maxEvaluations: Math.min(
          MAX_SPLIT_REGEX_EVALUATIONS_PER_REQUEST * batch.requests.length,
          MAX_SPLIT_REGEX_EVALUATIONS,
        ),
        slicer: createTimeSlicer(),
      });

      if (split.status === 'unmatched-names') {
        reportUnexpectedFallback('unmatched-names', () => ({
          ...describeBatch(batch),
          nameCount,
          unmatchedNameCount: split.unmatchedNameCount,
        }));
      }

      return split.status === 'split' ? split.namesByRequest : undefined;
    } catch (error) {
      reportUnexpectedFallback('exception', () => describeBatch(batch), error);

      return undefined;
    }
  }

  /**
   * Reports the failures of a batch query that merging the queries can cause. A 400 about a single
   * pattern fails the widget's own query too, any other 400 is reported. Any other failure is
   * reported once the own query of a widget of the batch succeeds: the merged query running out of
   * memory or time fails with a 500 (or a 504 from the proxy), like an outage, which fails the own
   * queries too and isn't reported.
   */
  async function reportFailedBatchQuery(
    error: unknown,
    batch: DefinitionsBatch<DefinitionsBatchRequest>,
  ): Promise<void> {
    try {
      if (!isResponseLike(error) || error.status !== 400) {
        const status = isResponseLike(error) ? error.status : undefined;
        const reportBatchFailure = () =>
          reportUnexpectedFallback('batch-query-failed', () => ({
            ...describeBatch(batch),
            status,
          }));

        batch.requests.forEach((request) =>
          batchFailureReportsByRequest.set(request, reportBatchFailure),
        );

        return;
      }

      const { errorType, title = '' } = parseErrorResponseBody(
        await error.json().catch(() => undefined),
      );

      if (title.startsWith(INVALID_PATTERN_ERROR_TITLE_PREFIX)) {
        return;
      }

      reportUnexpectedFallback(
        QUERY_TOO_LARGE_ERROR_TITLE.test(title) ? 'query-too-large' : 'query-rejected',
        () => ({ ...describeBatch(batch), status: error.status, errorType }),
      );
    } catch {
      // Reporting must not fail the widgets of the batch.
    }
  }

  /**
   * Reports a batch that fell back to the widgets' own queries for a reason that isn't expected
   * by design, once per reason, as it would otherwise repeat on every dashboard load. Batches cut
   * off or over the regex evaluation guard aren't reported. The context holds counts only: names,
   * patterns and run ids are user data.
   */
  function reportUnexpectedFallback(
    reason: UnexpectedFallbackReason,
    getContext: () => UnexpectedFallbackContext,
    error?: unknown,
  ) {
    try {
      if (reportedFallbackReasons.has(reason)) {
        return;
      }

      reportedFallbackReasons.add(reason);

      const context = getContext();
      const tags = { batchFallbackReason: reason };

      if (reason === 'exception') {
        logger.logException(error, { module: LOGGER_MODULE, extra: context }, tags);
      } else {
        logger.logMessage(
          `${LOGGER_MODULE}: definitions batch fell back to per-widget queries (${reason})`,
          { module: LOGGER_MODULE, ...context },
          tags,
        );
      }
    } catch {
      // Reporting must not fail the widgets of the batch.
    }
  }

  function createTimeSlicer(): TimeSlicer {
    let sliceStart = now();

    return {
      shouldYield: () => now() - sliceStart >= BATCH_SPLIT_SLICE_MS,
      yieldToEventLoop: async () => {
        await yieldToEventLoop();
        sliceStart = now();
      },
    };
  }

  /**
   * Returns all names matching any pattern of the batch, or undefined when they don't fit in one
   * page.
   */
  async function queryDefinitionsBatch({
    projectIds,
    experimentIds,
    patterns,
  }: DefinitionsBatch<DefinitionsBatchRequest>): Promise<string[] | undefined> {
    const { entries, nextPage } = await leaderboardClient.queryAttributeDefinitionsWithinProject({
      query: {
        nextPage: { limit: BATCH_PAGE_SIZE },
        attributeFilter: [{ attributeType: 'floatSeries' }],
        attributeNameFilter: {
          mustMatchAny: patterns.map((pattern) => ({ mustMatchRegexes: [pattern] })),
        },
        experimentIdsFilter: experimentIds,
        projectIdentifiers: projectIds,
      },
    });

    return nextPage?.nextPageToken ? undefined : entries.map(({ name }) => name);
  }

  async function resolveUnbatched({
    projectIds,
    experimentIds,
    patterns,
  }: DefinitionsBatchRequest): Promise<AttributeWidgetSource[]> {
    const { entries, truncated } = await queryAttributeDefinitionsMatchingAnyPattern({
      projectIds,
      experimentIds,
      patterns,
    });

    // A truncated combined result is cut in the server order, which can drop all matches of the
    // first sources, so resolve the sources one by one in their order instead.
    if (truncated && patterns.length > 1) {
      return mapFromAPIAttributesToDomain(
        await queryAttributeDefinitionsPatternByPattern({ projectIds, experimentIds, patterns }),
      );
    }

    return orderByFirstMatchingPattern(mapFromAPIAttributesToDomain(entries), patterns);
  }

  async function queryAttributeDefinitionsPatternByPattern({
    projectIds,
    experimentIds,
    patterns,
  }: {
    projectIds: string[];
    experimentIds: string[];
    patterns: string[];
  }) {
    const entriesByName = new Map<string, AttributeDefinitionDTO>();

    for (const pattern of patterns) {
      // useWidgetSourceAttributes returns
      // [...direct sources, ...resolved sources].slice(0, CHART_MAX_METRICS_LIMIT). Once
      // CHART_MAX_METRICS_LIMIT attributes are resolved, the next patterns can only append at
      // index >= CHART_MAX_METRICS_LIMIT, which is always cut off.
      if (entriesByName.size >= CHART_MAX_METRICS_LIMIT) {
        break;
      }

      const { entries } = await queryAttributeDefinitionsMatchingAnyPattern({
        projectIds,
        experimentIds,
        patterns: [pattern],
      });

      for (const entry of entries) {
        if (!entriesByName.has(entry.name)) {
          entriesByName.set(entry.name, entry);
        }
      }
    }

    return [...entriesByName.values()];
  }

  async function queryAttributeDefinitionsMatchingAnyPattern({
    projectIds,
    experimentIds,
    patterns,
  }: {
    projectIds: string[];
    experimentIds: string[];
    patterns: string[];
  }) {
    let nextPage: NextPageDTO | undefined;
    let entries: AttributeDefinitionDTO[] = [];

    do {
      const result = await leaderboardClient.queryAttributeDefinitionsWithinProject({
        query: {
          nextPage,
          attributeFilter: [{ attributeType: 'floatSeries' }],
          attributeNameFilter: {
            mustMatchAny: patterns.map((pattern) => ({ mustMatchRegexes: [pattern] })),
          },
          experimentIdsFilter: experimentIds,
          projectIdentifiers: projectIds,
        },
      });

      nextPage = result.nextPage;
      entries = entries.concat(result.entries);
    } while (nextPage?.nextPageToken && entries.length <= CHART_MAX_METRICS_LIMIT);

    return { entries, truncated: Boolean(nextPage?.nextPageToken) };
  }

  function mapFromAPIAttributesToDomain(
    attributes: AttributeDefinitionDTO[],
  ): AttributeWidgetSource[] {
    return mapNamesToDomain(attributes.map(({ name }) => name));
  }

  return { fromSources };
};

/**
 * Requests that can't be merged are resolved on their own: patterns re2js can't compile (the
 * result couldn't be split, and the server error should fail only this widget), an order that
 * depends on the server side run count, too many patterns or a query that alone exceeds the size
 * budget.
 */
function isBatchable(request: DefinitionsBatchRequest): boolean {
  return (
    request.projectIds.length > 0 &&
    request.experimentIds.length > 0 &&
    !hasAmbiguousExactMatchRanking(request) &&
    request.patterns.length <= MAX_PATTERNS_PER_BATCH &&
    request.patterns.every((pattern) => compileCaseInsensitivePattern(pattern) !== undefined) &&
    estimateDefinitionsQuerySizeBytes(request) <= DEFINITIONS_QUERY_SIZE_BUDGET_BYTES
  );
}

function describeBatch({
  projectIds,
  experimentIds,
  patterns,
  requests,
}: DefinitionsBatch<DefinitionsBatchRequest>) {
  return {
    requestCount: requests.length,
    patternCount: patterns.length,
    projectCount: projectIds.length,
    runCount: experimentIds.length,
  };
}

// The leaderboard client throws the Response of a failed request.
function isResponseLike(value: unknown): value is Pick<Response, 'status' | 'json'> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'status' in value &&
    typeof value.status === 'number' &&
    'json' in value &&
    typeof value.json === 'function'
  );
}

function parseErrorResponseBody(body: unknown): { errorType?: string; title?: string } {
  if (typeof body !== 'object' || body === null) {
    return {};
  }

  return {
    errorType:
      'errorType' in body && typeof body.errorType === 'string' ? body.errorType : undefined,
    title: 'title' in body && typeof body.title === 'string' ? body.title : undefined,
  };
}

function mapNamesToDomain(names: string[]): AttributeWidgetSource[] {
  return names.map((name) => ({
    type: WidgetSourceType.ATTRIBUTE,
    value: name,
    metadata: {
      attributeType: 'floatSeries',
    },
  }));
}

/**
 * Restores the source order of the widget: attributes are grouped by the first pattern they match,
 * keeping the server order within each group. Patterns are matched like the backend does it:
 * RE2, case-insensitive, partial match. Attributes that no pattern matches on the client side
 * (e.g. a pattern re2js can't compile) are kept at the end in the server order.
 */
function orderByFirstMatchingPattern(
  attributes: AttributeWidgetSource[],
  patterns: string[],
): AttributeWidgetSource[] {
  if (patterns.length < 2) {
    return attributes;
  }

  const compiledPatterns = patterns.map(compileCaseInsensitivePattern);
  const groups: AttributeWidgetSource[][] = patterns.map(() => []);
  const unmatched: AttributeWidgetSource[] = [];

  for (const attribute of attributes) {
    const patternIndex = compiledPatterns.findIndex((compiledPattern) =>
      compiledPattern?.matcher(attribute.value).find(),
    );

    if (patternIndex === -1) {
      unmatched.push(attribute);
    } else {
      groups[patternIndex].push(attribute);
    }
  }

  return [...groups.flat(), ...unmatched];
}

export const widgetAttributeResolver = createWidgetAttributeResolver();
